import { useMutation } from '@tanstack/react-query';
import { BaseQuery } from '../../types/query';
import {
  UserActivateAccountRequest,
  UserChangePasswordRequest,
  UserDeleteRequest,
  UserPermissionGridSaveRequest,
  UserRegisterRequest,
  UserRoleCreateRequest,
  UserRoleDeleteRequest,
  UserLoginOTPRequest,
  UserResetPasswordRequest,
  UserLockUnlockRequest,
  UserUpdateRequest,
  CitizenRegisterRequest,
} from '../../types/user';
import {
  activateUserAccount,
  changePassword,
  createUser,
  createUserRoles,
  deleteUser,
  deleteUserRoles,
  editUserRoles,
  editUserData,
  forgetPassword,
  getSystemPermissions,
  getUserDetails,
  getUserRolePermissions,
  getUserRoles,
  loginUser,
  updateUserPermissionGrid,
  userSendLoginOTP,
  userResendOTP,
  userSendForgetPasswordOTP,
  userResetPassword,
  userResendForgetPasswordOTP,
  userGetUserProfileDetails,
  userUpdateProfile,
  updateProfilePicture,
  getProfilePic,
  userDeleteProfilePicture,
  resendActivationRequest,
  lockUnlockUser,
  getUserAuditLog,
  registerCitizen,
} from '../services/userService';

export const useCreateUser = () => {
  return useMutation({
    mutationFn: (data: UserRegisterRequest) => createUser(data),
  });
};

export const useDeleteUser = () => {
  return useMutation({
    mutationFn: (data: UserDeleteRequest) => deleteUser(data),
  });
};

export const useLockUnlockkUser = () => {
  return useMutation({
    mutationFn: (data: UserLockUnlockRequest) => lockUnlockUser(data),
  });
};

export const useLoginUser = () => {
  return useMutation({
    mutationFn: (data: { email: string; password: string; deviceToken: string }) =>
      loginUser(data.email, data.password, data.deviceToken),
  });
};

export const useForgetPassword = () => {
  return useMutation({
    mutationFn: (data: { email: string }) => forgetPassword(data.email),
  });
};

export const useChangePassword = () => {
  return useMutation({
    mutationFn: (data: UserChangePasswordRequest) => changePassword(data),
  });
};

export const useActivateUserAccount = () => {
  return useMutation({
    mutationFn: (data: UserActivateAccountRequest) => activateUserAccount(data),
  });
};

export const useRegisterUser = () => {
  return useMutation({
    mutationFn: (data: CitizenRegisterRequest) => registerCitizen(data),
  });
};

export const useGetUser = () => {
  return useMutation({
    mutationFn: (data: any) => getUserDetails(data),
  });
};

export const useCreateUserRoles = () => {
  return useMutation({
    mutationFn: (data: UserRoleCreateRequest) => createUserRoles(data),
  });
};

export const useEditUserRoles = () => {
  return useMutation({
    mutationFn: (data: UserRoleCreateRequest) => editUserRoles(data),
  });
};

export const useGetUserRoles = () => {
  return useMutation({
    mutationFn: (data: BaseQuery) => getUserRoles(data),
  });
};

export const useDeleteUserRoles = () => {
  return useMutation({
    mutationFn: (data: UserRoleDeleteRequest) => deleteUserRoles(data),
  });
};

export const useGetSystemPermissions = () => {
  return useMutation({
    mutationFn: (data: BaseQuery) => getSystemPermissions(data),
  });
};

export const useGetUserRolePermissions = () => {
  return useMutation({
    mutationFn: (data: BaseQuery) => getUserRolePermissions(data),
  });
};

export const useUpdatePermissionGrid = () => {
  return useMutation({
    mutationFn: (data: UserPermissionGridSaveRequest[]) => updateUserPermissionGrid(data),
  });
};

export const useSendLoginOTP = () => {
  return useMutation({
    mutationFn: (data: UserLoginOTPRequest) => userSendLoginOTP(data),
  });
};

export const useSendForgetPasswordOTP = () => {
  return useMutation({
    mutationFn: (data: UserLoginOTPRequest) => userSendForgetPasswordOTP(data),
  });
};

export const useResendOTP = () => {
  return useMutation({
    mutationFn: (data: { otpId: number }) => userResendOTP(data.otpId),
  });
};

export const useResendForgetPasswordOTP = () => {
  return useMutation({
    mutationFn: (data: { otpId: number }) => userResendForgetPasswordOTP(data.otpId),
  });
};

export const useResetPassword = () => {
  return useMutation({
    mutationFn: (data: UserResetPasswordRequest) => userResetPassword(data),
  });
};

export const useGetUserProfileDetails = () => {
  return useMutation({
    mutationFn: (data: any) => userGetUserProfileDetails(data),
  });
};

export const updateUserProfile = () => {
  return useMutation({
    mutationFn: (data: any) => userUpdateProfile(data),
  });
};

export const updateProfilePic = () => {
  return useMutation({
    mutationFn: (data: any) => updateProfilePicture(data),
  });
};

export const deleteProfilePic = () => {
  return useMutation({
    mutationFn: (data: any) => userDeleteProfilePicture(data),
  });
};

export const useGetProfileImage = () => {
  return useMutation({
    mutationFn: (data: any) => getProfilePic(data),
  });
};

export const resendActivationEmail = () => {
  return useMutation({
    mutationFn: (data: any) => resendActivationRequest(data),
  });
};

export const useEditUser = () => {
  return useMutation({
    mutationFn: (data: UserUpdateRequest) => editUserData(data),
  });
};

export const useGetUserAuditLog = () => {
  return useMutation({
    mutationFn: (data: BaseQuery) => getUserAuditLog(data),
  });
};